import type { RecordingCreateInput } from "@rosc/contracts";
import { z } from "zod";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";

export type RecordingDraftBody = Omit<
  Extract<RecordingCreateInput, { transcription: string }>,
  "name"
>;
const identity = {
  version: z.literal(2),
  key: z.string().uuid(),
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
};
const schema = z.discriminatedUnion("kind", [
  z.object({ ...identity, kind: z.literal("pending") }).strict(),
  z
    .object({
      ...identity,
      kind: z.literal("accepted"),
      recordingPublicId: z.uuidv4().toLowerCase(),
    })
    .strict(),
]);
export type RecordingSubmission = z.infer<typeof schema>;
const prefix = "rosc:recording-submission:";
const storageKey = (account: string, parent: string): string =>
  `${prefix}${encodeURIComponent(account)}:${parent}`;
export const readRecordingSubmission = (
  account: string,
  parent: string,
): RecordingSubmission | null => {
  assertNoObsoleteSubmission(account, "rosc:recording-submission:");
  const value = sessionStorage.getItem(storageKey(account, parent));
  if (value === null) return null;
  try {
    return schema.parse(JSON.parse(value));
  } catch {
    throw new Error(
      "Saved recording submission cannot be read. Reselect this visit before abandoning it.",
    );
  }
};
const save = (
  account: string,
  parent: string,
  value: RecordingSubmission,
): void => {
  sessionStorage.setItem(storageKey(account, parent), JSON.stringify(value));
};
export const prepareRecordingSubmission = async (
  account: string,
  parent: string,
  body: RecordingDraftBody,
): Promise<RecordingSubmission> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(
      JSON.stringify([
        parent,
        Math.ceil(body.duration),
        body.language.trim().toLowerCase(),
        body.transcription,
        ...(body.recordingConsent ? [{ recordingConsent: true }] : []),
      ]),
    ),
  );
  const fingerprint = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  const existing = readRecordingSubmission(account, parent);
  if (existing) {
    if (existing.fingerprint !== fingerprint)
      throw new Error(
        "This recording submission has changed. Restore its original input or check this visit before abandoning it.",
      );
    return existing;
  }
  const pending = {
    version: 2,
    kind: "pending",
    key: crypto.randomUUID(),
    fingerprint,
  } satisfies RecordingSubmission;
  // Persist identity before transport; never store transcript or audio here.
  save(account, parent, pending);
  return pending;
};
export const completeRecordingSubmission = (
  account: string,
  parent: string,
  submission: RecordingSubmission,
  recordingPublicId: string,
): RecordingSubmission => {
  const accepted = {
    ...submission,
    version: 2,
    kind: "accepted",
    recordingPublicId,
  } satisfies RecordingSubmission;
  save(account, parent, accepted);
  return accepted;
};
export const abandonRecordingSubmission = (
  account: string,
  parent: string,
): void => {
  discardObsoleteSubmissions(account, "rosc:recording-submission:");
  sessionStorage.removeItem(storageKey(account, parent));
};
export const beginRecordingDraft = (account: string, parent: string): void => {
  if (readRecordingSubmission(account, parent)?.kind === "pending")
    throw new Error(
      "Reselect this visit before abandoning the previous recording submission and starting another.",
    );
  abandonRecordingSubmission(account, parent);
};
